import React, { useEffect, useState } from "react";
import { Button, TextField, List, ListItem, ListItemText, ListItemSecondaryAction, CircularProgress, Alert, Paper, Typography, Box, IconButton, FormControlLabel, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useStore } from "../../app/stores/store";
import { NotificationEmail, NotificationEmailFormValues } from "../../app/models/emailNotification";
import { useTranslation } from "react-i18next";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";

export default function NotificationEmailScreen() {
  const notificationEmailStore = useStore((s) => s.notificationEmailStore);
  const modalStore = useStore((s) => s.modalStore);
  const { emails, addEmail, removeEmail, fetchEmails, loading, reminderSettings, fetchReminderSettings, updateReminderSettings, runReminderNow } = notificationEmailStore;
  const [formData, setFormData] = useState<NotificationEmailFormValues>({
    email: "",
  });
  const [isEnabled, setIsEnabled] = useState(true);
  const [timeValue, setTimeValue] = useState("09:00");
  const [savingSettings, setSavingSettings] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchEmails();
    fetchReminderSettings();
  }, [fetchEmails, fetchReminderSettings]);

  useEffect(() => {
    if (!reminderSettings) return;
    setIsEnabled(reminderSettings.isEnabled);
    const hour = String(reminderSettings.runHour).padStart(2, "0");
    const minute = String(reminderSettings.runMinute).padStart(2, "0");
    setTimeValue(`${hour}:${minute}`);
  }, [reminderSettings]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formData.email) {
      await addEmail(formData);
      await fetchEmails();
      setFormData({ email: "" });
    }
  };

  const handleRemove = async (id: string) => {
    await removeEmail(id);
    await fetchEmails();
  };

  const handleSaveReminderSettings = async () => {
    const [hour, minute] = timeValue.split(":").map((x) => parseInt(x, 10));
    if (Number.isNaN(hour) || Number.isNaN(minute)) {
      return;
    }

    setSavingSettings(true);
    try {
      await updateReminderSettings({
        isEnabled,
        runHour: hour,
        runMinute: minute,
      });
      await fetchReminderSettings();
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      await runReminderNow();
      await fetchReminderSettings();
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t('notificationEmails.title')}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                value={formData.email}
                onChange={(e) => setFormData({ email: e.target.value })}
                placeholder={t('notificationEmails.enterEmail')}
                fullWidth
                required
                label={t('notificationEmails.enterEmail')}
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!formData.email}
              >
                {t('notificationEmails.addEmail')}
              </Button>
            </form>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('notificationEmails.pendingReminder')}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
              }
              label={t('notificationEmails.enableDailyReminder')}
            />
            <TextField
              type="time"
              label={t('notificationEmails.reminderTime')}
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1, mb: 2, width: 220 }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveReminderSettings}
                disabled={savingSettings}
              >
                {savingSettings ? t('general.loading') : t('notificationEmails.saveReminderSettings')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleRunNow}
                disabled={runningNow}
              >
                {runningNow ? t('general.loading') : t('notificationEmails.runNow')}
              </Button>
            </Box>
            {reminderSettings?.lastRunAt && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('notificationEmails.lastRun')}: {new Date(reminderSettings.lastRunAt).toLocaleString("de-DE")}
              </Typography>
            )}
          </Paper>

          {emails.length > 0 ? (
            <Paper sx={{ p: 2 }}>
              <List>
                {emails?.map((item: NotificationEmail, index: number) => (
                  <ListItem key={index} divider>
                    <ListItemText primary={item.email} />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={(event) => {
                          event.stopPropagation();
                          modalStore.openModal(
                            <ConfirmComponent
                              onYes={async () => {
                                handleRemove(item.id!);
                                modalStore.closeModal();
                              }}
                              onCancel={modalStore.closeModal}
                            />
                          );
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Alert severity="info">{t('notificationEmails.noEmails')}</Alert>
          )}
        </>
      )}
    </Box>
  );
}
